import type { ClientId } from "../../../shared/kernel/identifiers.js";
import type { NdtAssetRepository } from "../../../domain/ndt/ndt-asset-repository.js";
import type { NdtAsset, NdtAssetId, NdtAssetType } from "../../../domain/ndt/ndt-assets.js";
import type { 
  NdtAssetCertificate, 
  NdtAssetVerification, 
  NdtAssetUsePolicy, 
  NdtAssetOverrideLog, 
  NdtAssetSnapshot 
} from "../../../domain/ndt/ndt-asset-history.js";

export class InMemoryNdtAssetRepository implements NdtAssetRepository {
  private assets = new Map<string, NdtAsset>();
  private certificates = new Map<string, NdtAssetCertificate>();
  private verifications = new Map<string, NdtAssetVerification>();
  private policies = new Map<string, NdtAssetUsePolicy>();
  private overrideLogs = new Map<string, NdtAssetOverrideLog>();
  private snapshots = new Map<string, NdtAssetSnapshot>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    if (typeof localStorage === 'undefined') return;
    try {
      const a = localStorage.getItem('inspect_ndt_assets');
      if (a) this.assets = new Map(JSON.parse(a));
      
      const c = localStorage.getItem('inspect_ndt_certs');
      if (c) this.certificates = new Map(JSON.parse(c));
      
      const v = localStorage.getItem('inspect_ndt_verifications');
      if (v) this.verifications = new Map(JSON.parse(v));
      
      const p = localStorage.getItem('inspect_ndt_policies');
      if (p) this.policies = new Map(JSON.parse(p));
      
      const o = localStorage.getItem('inspect_ndt_overrides');
      if (o) this.overrideLogs = new Map(JSON.parse(o));
      
      const s = localStorage.getItem('inspect_ndt_snapshots');
      if (s) this.snapshots = new Map(JSON.parse(s));
    } catch (e) {
      console.warn("Failed to load NDT assets from localStorage", e);
    }
  }

  private persist() {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem('inspect_ndt_assets', JSON.stringify(Array.from(this.assets.entries())));
      localStorage.setItem('inspect_ndt_certs', JSON.stringify(Array.from(this.certificates.entries())));
      localStorage.setItem('inspect_ndt_verifications', JSON.stringify(Array.from(this.verifications.entries())));
      localStorage.setItem('inspect_ndt_policies', JSON.stringify(Array.from(this.policies.entries())));
      localStorage.setItem('inspect_ndt_overrides', JSON.stringify(Array.from(this.overrideLogs.entries())));
      localStorage.setItem('inspect_ndt_snapshots', JSON.stringify(Array.from(this.snapshots.entries())));
    } catch (e) {
      console.warn("Failed to save NDT assets to localStorage", e);
    }
  }

  // END assets (aparelhos, transdutores, yokes, B-Scan, blocos) are a GLOBAL
  // technical catalog: the same physical tool is used across clients, so
  // visibility is never filtered by clientId (Bloco 18). The clientId kept on
  // each record is provenance only — who registered it — never a filter.
  // Client-specific data (cliente, projeto, unidade, TAG, inspeção) stays on
  // the report, not on the tool.

  async getAsset(id: NdtAssetId): Promise<NdtAsset | null> {
    return this.assets.get(id) ?? null;
  }

  async listAssets(type?: NdtAssetType, includeInactive: boolean = false): Promise<NdtAsset[]> {
    let arr = Array.from(this.assets.values());
    if (type) {
      arr = arr.filter(a => a.assetType === type);
    }
    if (!includeInactive) {
      arr = arr.filter(a => a.status !== 'INACTIVE' && !a.deletedAt);
    } else {
      arr = arr.filter(a => !a.deletedAt);
    }
    return arr;
  }

  async saveAsset(asset: NdtAsset): Promise<void> {
    this.assets.set(asset.id, asset);
    this.persist();
  }

  async deleteAsset(id: NdtAssetId): Promise<void> {
    if (this.assets.has(id)) {
      this.assets.delete(id);
      this.persist();
    }
  }

  async saveCertificate(cert: NdtAssetCertificate): Promise<void> {
    this.certificates.set(cert.id, cert);
    this.persist();
  }

  async listCertificates(assetId: NdtAssetId): Promise<NdtAssetCertificate[]> {
    return Array.from(this.certificates.values())
      .filter(c => c.assetId === assetId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async saveVerification(ver: NdtAssetVerification): Promise<void> {
    this.verifications.set(ver.id, ver);
    this.persist();
  }

  async listVerifications(assetId: NdtAssetId): Promise<NdtAssetVerification[]> {
    return Array.from(this.verifications.values())
      .filter(v => v.assetId === assetId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async getPolicy(assetType: NdtAssetType | 'ALL', condition: string): Promise<NdtAssetUsePolicy | null> {
    // For in-memory, we can just search.
    // Assuming policies are global or per client? For now, policies are stored normally but we might have a global default.
    const arr = Array.from(this.policies.values());
    const exact = arr.find(p => p.assetType === assetType && p.condition === condition);
    if (exact) return exact;
    const fallback = arr.find(p => p.assetType === 'ALL' && p.condition === condition);
    return fallback || null;
  }

  async savePolicy(policy: NdtAssetUsePolicy): Promise<void> {
    this.policies.set(policy.id, policy);
    this.persist();
  }

  async saveOverrideLog(log: NdtAssetOverrideLog): Promise<void> {
    this.overrideLogs.set(log.id, log);
    this.persist();
  }

  async listOverrideLogs(clientId: ClientId, assetId: NdtAssetId): Promise<NdtAssetOverrideLog[]> {
    return Array.from(this.overrideLogs.values())
      .filter(l => l.clientId === clientId && l.assetId === assetId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  async saveSnapshot(snapshot: NdtAssetSnapshot): Promise<void> {
    this.snapshots.set(snapshot.id, snapshot);
    this.persist();
  }

  async getSnapshot(clientId: ClientId, id: string): Promise<NdtAssetSnapshot | null> {
    const s = this.snapshots.get(id);
    return (s && s.clientId === clientId) ? s : null;
  }
}
